<template>
  <div class="referral-program">
    <!-- Hero Section with Gradient Background -->
    <div class="hero-section">
      <div class="container">
        <!-- Logo -->
        <div class="logo-section">
          <img src="/logo.png" alt="AutoSyncX" class="logo" v-if="logoExists" />
          <h1 class="logo-text" v-else>AutoSyncX</h1>
        </div>

        <!-- Main Heading -->
        <h2 class="subtitle">REFERRAL REWARDS PROGRAM</h2>
        <h1 class="main-title">WIN ${{ formatNumber(totalAmount) }}</h1>
        <h2 class="sub-title">FOR JUST REFERRING</h2>
        <p class="bonus-text">+ BONUS TO WHOEVER REFERS THE MOST</p>

        <!-- CTA Buttons -->
        <div class="cta-buttons">
          <button class="btn-signup" @click="goToSignup">Sign Up Now</button>
          <button class="btn-signin" @click="goToSignin">Sign In</button>
        </div>

        <!-- Countdown Timer -->
        <div class="countdown-section">
          <p class="countdown-label">Watch countdown live</p>
          <div class="countdown-timer">
            <div class="time-box">
              <div class="time-value">{{ countdown.days }}</div>
              <div class="time-label">DAYS</div>
            </div>
            <div class="time-box">
              <div class="time-value">{{ countdown.hours }}</div>
              <div class="time-label">HOURS</div>
            </div>
            <div class="time-box">
              <div class="time-value">{{ countdown.minutes }}</div>
              <div class="time-label">MIN</div>
            </div>
            <div class="time-box">
              <div class="time-value">{{ countdown.seconds }}</div>
              <div class="time-label">SEC</div>
            </div>
          </div>
        </div>

        <!-- Total Signups -->
        <div class="total-signups">
          <p class="signups-label">Total signups</p>
          <h1 class="signups-value">{{ totalSignups }}</h1>
        </div>

        <!-- Bottom Stats -->
        <div class="stats-section">
          <div class="stat-item">
            <p class="stat-label">Referrals needed to win</p>
            <h2 class="stat-value">{{ referralsNeeded }}</h2>
          </div>
          <div class="stat-item">
            <p class="stat-label">Total payout</p>
            <h2 class="stat-value">${{ totalPayout }}</h2>
            <p class="bonus-pool">+${{ bonusAmount }} remaining in bonus pool</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

export default {
  name: 'ReferralProgram',
  data() {
    return {
      totalSignups: 0,
      referralGoal: 1000,
      amountPerReferral: 3.00,
      bonusAmount: 500,
      countdownEnd: null,
      countdown: {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      },
      countdownInterval: null,
      logoExists: false
    };
  },
  computed: {
    totalAmount() {
      return (this.referralGoal * this.amountPerReferral).toFixed(0);
    },
    referralsNeeded() {
      return Math.max(0, this.referralGoal - this.totalSignups);
    },
    totalPayout() {
      return (this.totalSignups * this.amountPerReferral).toFixed(0);
    }
  },
  methods: {
    async loadReferralStats() {
      try {
        const response = await axios({
          method: "GET",
          url: "/api/public-referral-stats",
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data.flag === 1) {
          const data = response.data.data;
          this.totalSignups = data.total_signups || 0;
          this.referralGoal = data.referral_goal || 1000;
          this.amountPerReferral = parseFloat(data.amount_per_referral) || 3.00;
          this.bonusAmount = parseFloat(data.bonus_amount) || 500;
          this.countdownEnd = data.countdown_end;
          
          // Start countdown
          this.updateCountdown();
        }
      } catch (error) {
        console.error('Error loading referral stats:', error);
      }
    },
    
    updateCountdown() {
      if (!this.countdownEnd) {
        this.countdown = { days: 0, hours: 0, minutes: 0, seconds: 0 };
        return;
      }
      
      const now = new Date().getTime();
      const endDate = new Date(this.countdownEnd).getTime();
      const distance = endDate - now;
      
      if (distance < 0) {
        this.countdown = { days: 0, hours: 0, minutes: 0, seconds: 0 };
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
        }
        return;
      }
      
      this.countdown.days = Math.floor(distance / (1000 * 60 * 60 * 24));
      this.countdown.hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      this.countdown.minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      this.countdown.seconds = Math.floor((distance % (1000 * 60)) / 1000);
    },
    
    formatNumber(num) {
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    checkLogo() {
      // Check if logo exists
      const img = new Image();
      img.onload = () => {
        this.logoExists = true;
      };
      img.onerror = () => {
        this.logoExists = false;
      };
      img.src = '/logo.png';
    },

    goToSignup() {
      // Redirect to conversational agent signup
      this.$router.push('/signup-agent');
    },

    goToSignin() {
      this.$router.push('/login');
    }
  },
  mounted() {
    this.loadReferralStats();
    this.checkLogo();
    
    // Update countdown every second
    this.countdownInterval = setInterval(() => {
      this.updateCountdown();
    }, 1000);
  },
  beforeDestroy() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }
};
</script>

<style scoped>
.referral-program {
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
}

.hero-section {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 25%, #0f3460 50%, #533483 75%, #7b2869 100%);
  min-height: 100vh;
  padding: 40px 20px;
  position: relative;
  overflow: hidden;
}

.hero-section::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -20%;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(255, 64, 129, 0.3) 0%, transparent 70%);
  border-radius: 50%;
  filter: blur(60px);
}

.hero-section::after {
  content: '';
  position: absolute;
  bottom: -30%;
  left: -10%;
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(138, 43, 226, 0.3) 0%, transparent 70%);
  border-radius: 50%;
  filter: blur(60px);
}

.container {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 1;
}

.logo-section {
  margin-bottom: 10px;
}

.logo {
  height: 140px;
  width: auto;
}

.logo-text {
  font-size: 36px;
  font-weight: bold;
  color: white;
  margin: 0;
}

.subtitle {
  font-size: 24px;
  font-weight: 600;
  color: #64b5f6;
  letter-spacing: 3px;
  margin: 0 0 20px 0;
}

.main-title {
  font-size: 80px;
  font-weight: 900;
  color: white;
  margin: 0;
  line-height: 1;
}

.sub-title {
  font-size: 36px;
  font-weight: 700;
  color: white;
  margin: 10px 0 20px 0;
}

.bonus-text {
  font-size: 16px;
  color: #90caf9;
  margin: 0 0 30px 0;
  font-weight: 500;
}

.cta-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin: 30px 0 50px 0;
  flex-wrap: wrap;
}

.btn-signup,
.btn-signin {
  padding: 15px 40px;
  font-size: 18px;
  font-weight: 700;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.btn-signup {
  background: linear-gradient(135deg, #ff4081 0%, #ff6b9d 100%);
  color: white;
}

.btn-signup:hover {
  background: linear-gradient(135deg, #ff6b9d 0%, #ff4081 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 64, 129, 0.4);
}

.btn-signin {
  background: transparent;
  color: white;
  border: 2px solid white;
}

.btn-signin:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 255, 255, 0.2);
}

.countdown-section {
  margin: 50px 0;
}

.countdown-label {
  font-size: 18px;
  color: white;
  margin-bottom: 20px;
  font-weight: 500;
}

.countdown-timer {
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
}

.time-box {
  background: rgba(0, 0, 0, 0.5);
  border-radius: 12px;
  padding: 20px;
  min-width: 100px;
  backdrop-filter: blur(10px);
}

.time-value {
  font-size: 48px;
  font-weight: 900;
  color: white;
  line-height: 1;
}

.time-label {
  font-size: 14px;
  color: #90caf9;
  margin-top: 8px;
  font-weight: 600;
  letter-spacing: 1px;
}

.total-signups {
  margin: 50px 0;
}

.signups-label {
  font-size: 20px;
  color: white;
  margin-bottom: 15px;
  font-weight: 500;
}

.signups-value {
  font-size: 80px;
  font-weight: 900;
  color: white;
  margin: 0;
}

.stats-section {
  display: flex;
  justify-content: space-around;
  gap: 30px;
  margin-top: 50px;
  flex-wrap: wrap;
}

.stat-item {
  flex: 1;
  min-width: 250px;
}

.stat-label {
  font-size: 18px;
  color: white;
  margin-bottom: 10px;
  font-weight: 500;
}

.stat-value {
  font-size: 64px;
  font-weight: 900;
  color: white;
  margin: 0;
}

.bonus-pool {
  font-size: 14px;
  color: #90caf9;
  margin-top: 10px;
  font-weight: 500;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .hero-section {
    padding: 20px 15px;
  }

  .logo-section {
    margin-bottom: 20px;
  }

  .logo {
    height: 80px;
  }

  .logo-text {
    font-size: 28px;
  }

  .subtitle {
    font-size: 16px;
    letter-spacing: 2px;
    margin: 0 0 15px 0;
  }

  .main-title {
    font-size: 56px;
    margin: 10px 0;
  }

  .sub-title {
    font-size: 22px;
    margin: 10px 0 15px 0;
  }

  .bonus-text {
    font-size: 13px;
    margin: 0 0 20px 0;
  }

  .cta-buttons {
    display: flex;
    justify-content: center!important;
    gap: 12px;
    margin: 20px 0 30px 0;
  }

  .btn-signup,
  .btn-signin {
    padding: 5px 5px;
    font-size: 5px;
    min-width: 100px!important;
  }

  .countdown-section {
    margin: 30px 0;
  }

  .countdown-label {
    font-size: 16px;
    margin-bottom: 15px;
  }

  .countdown-timer {
    gap: 8px;
  }

  .time-box {
    min-width: 70px;
    padding: 12px 8px;
  }

  .time-value {
    font-size: 32px;
  }

  .time-label {
    font-size: 10px;
  }

  .total-signups {
    margin: 30px 0;
  }

  .signups-label {
    font-size: 18px;
    margin-bottom: 12px;
  }

  .signups-value {
    font-size: 64px;
  }

  .stats-section {
    gap: 25px;
    margin-top: 30px;
  }

  .stat-item {
    min-width: 100%;
  }

  .stat-label {
    font-size: 16px;
  }

  .stat-value {
    font-size: 52px;
  }

  .bonus-pool {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .hero-section {
    padding: 15px 12px;
  }

  .logo-section {
    margin-bottom: 15px;
  }

  .logo {
    height: 60px;
  }

  .logo-text {
    font-size: 24px;
  }

  .subtitle {
    font-size: 14px;
    letter-spacing: 1.5px;
    margin: 0 0 12px 0;
  }

  .main-title {
    font-size: 42px;
    margin: 8px 0;
  }

  .sub-title {
    font-size: 18px;
    margin: 8px 0 12px 0;
  }

  .bonus-text {
    font-size: 12px;
    margin: 0 0 15px 0;
  }

  .cta-buttons {
    gap: 10px;
    margin: 15px 0 25px 0;
  }

  .btn-signup,
  .btn-signin {
    width: 100%;
    max-width: 260px;
    padding: 13px 30px;
    font-size: 14px;
  }

  .countdown-section {
    margin: 25px 0;
  }

  .countdown-label {
    font-size: 15px;
    margin-bottom: 12px;
  }

  .countdown-timer {
    gap: 6px;
  }

  .time-box {
    min-width: 65px;
    padding: 10px 6px;
  }

  .time-value {
    font-size: 28px;
  }

  .time-label {
    font-size: 9px;
    margin-top: 6px;
  }

  .total-signups {
    margin: 25px 0;
  }

  .signups-label {
    font-size: 16px;
    margin-bottom: 10px;
  }

  .signups-value {
    font-size: 56px;
  }

  .stats-section {
    gap: 20px;
    margin-top: 25px;
  }

  .stat-label {
    font-size: 15px;
    margin-bottom: 8px;
  }

  .stat-value {
    font-size: 44px;
  }

  .bonus-pool {
    font-size: 12px;
    margin-top: 8px;
  }
}
</style>

